import type { CustomerIndexItemDto, CustomerIndexResponseDto } from '@techgitter/contracts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CACHE_KEY = 'customer_index_cache_v2';
const TTL_MS = 86_400_000; // 24 hours
const SCHEMA_VERSION = 2;
const API_URL = '/api/customers/index';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CacheEnvelope {
  cachedAt: number;       // Unix ms
  ttlMs: number;
  generatedAtUtc: string;
  schemaVersion: number;
  items: CustomerIndexItemDto[];
}

export interface GetCustomerIndexOptions {
  /** Bypass cache and fetch from network */
  forceRefresh?: boolean;
  /** If true, return stale cache even when a refresh is attempted */
  allowStale?: boolean;
}

export interface GetCustomerIndexResult {
  items: CustomerIndexItemDto[];
  generatedAtUtc: string;
  isStale?: boolean;
  warning?: string;
}

export interface RefreshResult {
  success: boolean;
  items?: CustomerIndexItemDto[];
  error?: string;
}

// ---------------------------------------------------------------------------
// Module-level state (in-memory tier)
// ---------------------------------------------------------------------------

let memoryCache: CacheEnvelope | null = null;
let inflightRequest: Promise<GetCustomerIndexResult> | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isFresh(envelope: CacheEnvelope): boolean {
  return Date.now() - envelope.cachedAt < envelope.ttlMs;
}

function readLocalStorage(): CacheEnvelope | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CacheEnvelope = JSON.parse(raw);
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocalStorage(envelope: CacheEnvelope): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage full or unavailable – silent fallback to memory-only
  }
}

function buildEnvelope(res: CustomerIndexResponseDto): CacheEnvelope {
  return {
    cachedAt: Date.now(),
    ttlMs: TTL_MS,
    generatedAtUtc: res.generatedAtUtc,
    schemaVersion: SCHEMA_VERSION,
    items: res.items,
  };
}

function storeEnvelope(envelope: CacheEnvelope): void {
  memoryCache = envelope;
  writeLocalStorage(envelope);
}

/** Resolve the best available cached envelope (memory → localStorage). */
function resolveCache(): CacheEnvelope | null {
  if (memoryCache) return memoryCache;
  const stored = readLocalStorage();
  if (stored) {
    memoryCache = stored; // promote to memory
  }
  return stored;
}

async function fetchFromNetwork(signal?: AbortSignal): Promise<CustomerIndexResponseDto> {
  const response = await fetch(API_URL, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Customer index fetch failed: ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch the customer index using two-tier cache with stale fallback.
 *
 * Policy:
 * - Fresh cache → return immediately (no network)
 * - Expired cache → try network; on failure return stale with warning
 * - No cache → fetch from network; throw if network also fails
 *
 * Concurrent calls share a single in-flight request.
 */
export async function getCustomerIndexCached(
  options?: GetCustomerIndexOptions,
): Promise<GetCustomerIndexResult> {
  const { forceRefresh = false, allowStale = true } = options ?? {};

  // --- Fast path: fresh in-memory / localStorage cache ---
  if (!forceRefresh) {
    const cached = resolveCache();
    if (cached && isFresh(cached)) {
      return {
        items: cached.items,
        generatedAtUtc: cached.generatedAtUtc,
      };
    }
  }

  // --- Deduplicate concurrent network requests ---
  if (inflightRequest && !forceRefresh) {
    return inflightRequest;
  }

  const networkPromise = (async (): Promise<GetCustomerIndexResult> => {
    try {
      const res = await fetchFromNetwork();
      const envelope = buildEnvelope(res);
      storeEnvelope(envelope);
      return { items: res.items, generatedAtUtc: res.generatedAtUtc };
    } catch (err: any) {
      // Stale fallback
      if (allowStale) {
        const stale = resolveCache();
        if (stale) {
          return {
            items: stale.items,
            generatedAtUtc: stale.generatedAtUtc,
            isStale: true,
            warning: `Using cached customer index (network error: ${err.message})`,
          };
        }
      }
      throw err;
    } finally {
      inflightRequest = null;
    }
  })();

  inflightRequest = networkPromise;
  return networkPromise;
}

/**
 * Force-refresh the cache. Returns the new data or an error.
 */
export async function refreshCustomerIndexCache(): Promise<RefreshResult> {
  try {
    const res = await fetchFromNetwork();
    const envelope = buildEnvelope(res);
    storeEnvelope(envelope);
    return { success: true, items: res.items };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Clear client-side caches (no server-side cache for customer index).
 */
export function invalidateCustomerIndexCache(): void {
  memoryCache = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Insert or replace an item in the cache by dataverseCustomerId.
 *
 * Used after create/update to bridge the Dataverse saved-query indexing lag —
 * the API may not return the just-written record on an immediate refetch, so we
 * upsert it into the cache directly. No-op when no cache envelope exists yet.
 */
export function upsertCustomerIndexItem(item: CustomerIndexItemDto): void {
  const envelope = resolveCache();
  if (!envelope) return;
  const idx = envelope.items.findIndex(
    (c) => c.dataverseCustomerId === item.dataverseCustomerId,
  );
  const items = idx >= 0
    ? envelope.items.map((c, i) => (i === idx ? item : c))
    : [...envelope.items, item];
  storeEnvelope({ ...envelope, items });
}

// ---------------------------------------------------------------------------
// Cache metadata (used by DebugPanel)
// ---------------------------------------------------------------------------

export function getCustomerIndexCacheMetadata(): {
  cachedAt: number;
  ttlMs: number;
  generatedAtUtc: string;
  recordCount: number;
} | null {
  const envelope = resolveCache();
  if (!envelope) return null;
  return {
    cachedAt: envelope.cachedAt,
    ttlMs: envelope.ttlMs,
    generatedAtUtc: envelope.generatedAtUtc,
    recordCount: envelope.items.length,
  };
}

// ---------------------------------------------------------------------------
// Test helpers (exported for unit tests only)
// ---------------------------------------------------------------------------

/** @internal Reset all module state for tests (memory + localStorage). */
export function _resetForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/** @internal Clear only the in-memory cache (simulates page reload). */
export function _clearMemoryForTesting(): void {
  memoryCache = null;
  inflightRequest = null;
}
